import type { Melody, MelodyGrid } from '@tunasong/schemas'
import type { EasyScoreDuration } from '../easy-score/duration.js'
import { isTripletGrid, melodyToSuggestion, TRIPLET_RATIO } from './convert.js'
import { barBeats, GRID_TOLERANCE } from './grid.js'

/**
 * Flatten a melody into an EasyScore `lang` string for an ADDITIVE staff sibling (§3).
 *
 * The sibling is a VIEW: the melody stays the source of truth, and the flattening is lossy on
 * purpose. Every loss is reported rather than swallowed, so the command can tell the user what
 * the staff will not show:
 *
 *   - `offsets-dropped` - notation has no way to draw "12 ms late".
 *   - `duration-rounded` - a length no single w/h/q/8/16 value, plain or dotted, can draw. That
 *     is a tied value (2.5 beats), or any note on a triplet grid, which has no bracket here.
 *   - `rest-dropped`    - a gap between notes has no note to hang on, and EasyScore rests are not
 *     modelled here. The staff therefore looks rhythmically denser than the melody sounds.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §3
 */

export interface MelodyRenderLoss {
  kind: 'offsets-dropped' | 'duration-rounded' | 'rest-dropped' | 'out-of-range'
  noteIndex?: number
  message: string
}

/**
 * The values a staff can draw a note as, in quarter-note beats. SHORTEST first, so `reduce`'s
 * strict `<` keeps the earlier entry on an exact tie and the tie resolves SHORT - the rule
 * `convert.ts` applies: a lengthened note overruns the next onset, a shortened one never does.
 *
 * Durations are read from the melody's exact beats, NOT through the AI suggestion enum
 * (`melodyDurations`): that enum is the vocabulary a model may answer in, and it has no dots. A
 * melody captured or edited by hand holds a dotted quarter as 1.5 beats; squeezed through the enum
 * it came out a quarter and left the bar short.
 */
const NOTATED_VALUES: { beats: number; token: EasyScoreDuration; dotted: boolean }[] = [
  { beats: 0.25, token: '16', dotted: false },
  { beats: 0.5, token: '8', dotted: false },
  { beats: 0.75, token: '8.', dotted: true },
  { beats: 1, token: 'q', dotted: false },
  { beats: 1.5, token: 'q.', dotted: true },
  { beats: 2, token: 'h', dotted: false },
  { beats: 3, token: 'h.', dotted: true },
  { beats: 4, token: 'w', dotted: false },
]

/**
 * The value a note of `beats` is drawn as. On a triplet grid the length is first scaled up by 3/2
 * into the value a reader sees under the bracket, and only undotted values are candidates: a dot
 * inside a tuplet is rare, and a full beat (1.5 notated) drawn as a dotted quarter would silently
 * read the triplet grid as straight.
 */
const notatedValue = (beats: number, grid: MelodyGrid): { token: EasyScoreDuration; beats: number } => {
  const triplet = isTripletGrid(grid)
  const notated = triplet ? beats * TRIPLET_RATIO : beats
  const candidates = triplet ? NOTATED_VALUES.filter(v => !v.dotted) : NOTATED_VALUES
  const best = candidates.reduce((carry, entry) =>
    Math.abs(entry.beats - notated) < Math.abs(carry.beats - notated) ? entry : carry
  )
  return { token: best.token, beats: best.beats }
}

export const melodyToScoreLang = (melody: Melody): { lang: string; losses: MelodyRenderLoss[] } => {
  const losses: MelodyRenderLoss[] = []
  if (melody.notes.length === 0) {
    return { lang: '', losses }
  }

  if (melody.notes.some(n => Math.round(Math.abs(n.offsetMs)) > 0)) {
    losses.push({
      kind: 'offsets-dropped',
      message: 'The staff cannot show the timing offsets - the melody keeps them.',
    })
  }

  const suggestion = melodyToSuggestion(melody)
  const values = melody.notes.map(note => notatedValue(note.duration, melody.grid))
  melody.notes.forEach((source, index) => {
    if (Math.abs(values[index].beats - source.duration) > GRID_TOLERANCE) {
      losses.push({
        kind: 'duration-rounded',
        noteIndex: index,
        message: `Note ${index + 1}: ${source.duration} beats drawn as ${values[index].token}.`,
      })
    }
    const previous = melody.notes[index - 1]
    if (previous && source.onset - (previous.onset + previous.duration) > GRID_TOLERANCE) {
      losses.push({
        kind: 'rest-dropped',
        noteIndex: index,
        message: `Note ${index + 1}: the rest before it is not drawn.`,
      })
    }
  })

  const perBar = barBeats(melody.tempo.meter)
  const bars: string[][] = []
  suggestion.forEach((s, index) => {
    const bar = Math.floor(melody.notes[index].onset / perBar)
    bars[bar] = [...(bars[bar] ?? []), `${s.pitch}${s.octave}/${values[index].token}`]
  })

  return {
    lang: bars
      .filter(Boolean)
      .map(bar => bar.join(', '))
      .join('\n'),
    losses,
  }
}
